import { Icon, IconType } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

export type SafeguardingWarningVariant =
  | 'capability'
  | 'role'
  | 'privacy-officer'

export type SafeguardingWarningAlertProps = {
  variant: SafeguardingWarningVariant
  siteNames: string[]
  testId?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ffa39f;
  border-radius: 8px;
  background-color: #fff5f6;
  color: ${({ theme }) => theme.color.status.danger};
  font-size: 16px;
  line-height: 24px;
`

const TitleRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: flex-start;
  font-weight: 500;
`

const SiteList = styled.ul`
  margin: 0;
  padding-left: 24px;
  list-style: disc;
`

const SafeguardingWarningAlert = ({
  variant,
  siteNames,
  testId
}: SafeguardingWarningAlertProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const isPrivacyOfficer = variant === 'privacy-officer'
  // The privacy-officer block and the safeguarding role change share the same
  // "Role cannot be changed." title; only the capability-toggle case differs.
  const title =
    variant === 'capability'
      ? reportsConstants.EDIT_PANEL_SAFEGUARDING_WARNING_TITLE_CAPABILITY
      : reportsConstants.EDIT_PANEL_SAFEGUARDING_WARNING_TITLE_ROLE
  const intro = isPrivacyOfficer
    ? reportsConstants.EDIT_PANEL_PRIVACY_OFFICER_WARNING_INTRO
    : reportsConstants.EDIT_PANEL_SAFEGUARDING_WARNING_INTRO
  const resolution = isPrivacyOfficer
    ? reportsConstants.EDIT_PANEL_PRIVACY_OFFICER_WARNING_RESOLUTION
    : reportsConstants.EDIT_PANEL_SAFEGUARDING_WARNING_RESOLUTION

  return (
    <Container data-testid={testId} role="alert">
      <TitleRow>
        <Icon customIcon={IconType.Attention} />
        <span>{t(title)}</span>
      </TitleRow>
      <span>{t(intro)}</span>
      <SiteList>
        {siteNames.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </SiteList>
      <span>{t(resolution)}</span>
    </Container>
  )
}

export default SafeguardingWarningAlert
